import type { AxiosError } from "axios";
import type { AdminTicket } from "@sd/types/admin";
import ServiceDeskAPIService from "@sd/services/APIs";
import {
  activityEntryMatchesTicket,
  parseTicketActivityEntries,
  sortActivityEntriesChronological,
  type TicketActivityEntry,
} from "@sd/lib/ticketDetailActivity";

export type TicketHistorySource = "ticket_history" | "activity_logs";

export type TicketHistoryFetchResult = {
  raw: unknown;
  source: TicketHistorySource;
};

function isNotFoundError(err: unknown): boolean {
  const status = (err as AxiosError)?.response?.status;
  return status === 404;
}

const silentRequest = { skipErrorNotification: true as const };

/**
 * GET `/service_desk/ticket_history/?ticket_id=&page=&page_size=`
 * Falls back to `/service_desk/activity-logs/` on 404 only.
 */
export async function fetchTicketDetailHistory(
  ticketId: string
): Promise<TicketHistoryFetchResult> {
  const normalizedTicketId = ticketId.trim();
  if (!normalizedTicketId) {
    return { raw: null, source: "ticket_history" };
  }

  try {
    const res = await ServiceDeskAPIService.ticketHistory.list(
      {
        ticket_id: normalizedTicketId,
        page: 1,
        page_size: 200,
      },
      silentRequest
    );
    return { raw: res.data, source: "ticket_history" };
  } catch (err) {
    if (!isNotFoundError(err)) {
      throw err;
    }
  }

  try {
    const res = await ServiceDeskAPIService.activityLogs.list(
      {
        ticket_id: normalizedTicketId,
        page: 1,
        page_size: 200,
      },
      silentRequest
    );
    return { raw: res.data, source: "activity_logs" };
  } catch (err) {
    if (isNotFoundError(err)) {
      return { raw: null, source: "activity_logs" };
    }
    throw err;
  }
}

function buildFallbackActivityEntries(
  ticket: AdminTicket,
  detailPayload: Record<string, unknown>
): TicketActivityEntry[] {
  const uiRef = ticket.uiId ?? ticket.id;
  const createdOn = String(
    detailPayload.created_on ?? detailPayload.created_at ?? ticket.createdAt ?? ""
  ).trim();
  const updatedOn = String(
    detailPayload.updated_on ?? detailPayload.updated_at ?? ticket.updatedAt ?? ""
  ).trim();
  const createdBy = String(
    detailPayload.created_by_name ??
      detailPayload.created_by ??
      detailPayload.requester_name ??
      ticket.requester ??
      ""
  ).trim();
  const entries: TicketActivityEntry[] = [];

  if (createdOn) {
    entries.push({
      id: `created-${ticket.id}`,
      activity_title: `Ticket ${uiRef} created`,
      activity_description: "",
      activity_type: "create",
      created_on: createdOn,
      created_by: createdBy,
    });
  }

  if (updatedOn && updatedOn !== createdOn) {
    entries.push({
      id: `updated-${ticket.id}`,
      activity_title: `Ticket ${uiRef} updated`,
      activity_description: `Status: ${ticket.status}`,
      activity_type: "update",
      created_on: updatedOn,
      created_by: createdBy,
    });
  }

  return entries;
}

export function parseTicketDetailHistoryEntries(
  history: TicketHistoryFetchResult | undefined,
  ticket: AdminTicket | null,
  detailPayload: Record<string, unknown> | undefined
): TicketActivityEntry[] {
  if (!ticket) return [];

  const parsed = parseTicketActivityEntries(history?.raw);
  const scoped =
    history?.source === "activity_logs"
      ? parsed.filter(entry => activityEntryMatchesTicket(entry, ticket))
      : parsed;

  if (scoped.length > 0) {
    return sortActivityEntriesChronological(scoped);
  }

  if (detailPayload) {
    return sortActivityEntriesChronological(
      buildFallbackActivityEntries(ticket, detailPayload)
    );
  }

  return [];
}
